import * as React from "react";
import { Box, Typography, Stack } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";


type PillarCardProps = {
  title: string;
  color: string;
  icon: React.ReactNode;
  items: string[];
  onClick?: () => void;
  enabled?: boolean;
  active?: boolean;
  completed?: boolean;
  isTablet?: boolean;
  statusLabel?: string;
};

export const PillarCard = ({
  title,
  color,
  icon,
  items,
  onClick,
  enabled = false,
  active = false,
  completed = false,
  isTablet = false,
  statusLabel,
}: PillarCardProps) => {
  const isDisabled = !enabled;

  return (
    <Box
      onClick={enabled ? onClick : undefined}
      role={enabled && onClick ? "button" : undefined}
      tabIndex={enabled && onClick ? 0 : -1}
      onKeyDown={(e) => {
        if (!enabled || !onClick) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        boxSizing: "border-box",
        flex: isTablet ? "none" : { xs: "none", md: 1 },
        width: "100%",
        minWidth: { xs: "100%", md: 250 },
        maxWidth: isTablet ? 620 : { xs: "100%", md: 360 },
        minHeight: isTablet ? 128 : { xs: 108, md: 330 },

        borderRadius: { xs: 5, sm: 6, md: 14 },
        px: isTablet ? 3.5 : { xs: 2.5, sm: 3.5, md: 3.5 },
        py: isTablet ? 2.75 : { xs: 2.25, sm: 3, md: 3 },

        backdropFilter: "blur(20px)",
        background: `linear-gradient(135deg, ${color}14, rgba(255,255,255,0.02))`,
        border: `2px solid ${color}66`,

        display: "flex",
        flexDirection: isTablet ? "row" : { xs: "row", md: "column" },
        alignItems: "center",
        justifyContent: "flex-start",
        textAlign: isTablet ? "left" : { xs: "left", md: "center" },
        gap: isTablet ? 3 : { xs: 2, md: 0 },

        cursor: enabled ? "pointer" : "default",
        opacity: isDisabled ? 0.25 : 1,
        filter: isDisabled ? "grayscale(0.7)" : "none",
        pointerEvents: isDisabled ? "none" : "auto",

        boxShadow: active
          ? `0 0 35px ${color}cc, 0 0 80px ${color}55`
          : completed
          ? `0 0 15px ${color}66`
          : `0 0 10px ${color}22`,

        transition: "all 0.3s ease",

        "&:hover": enabled
          ? {
              transform: "translateY(-4px) scale(1.01)",
              borderColor: `${color}cc`,
              boxShadow: `0 0 40px ${color}aa, 0 0 80px ${color}44`,
            }
          : {},
      }}
    >
      <Box
        sx={{
          width: isTablet ? 74 : { xs: 56, sm: 66, md: 80 },
          height: isTablet ? 74 : { xs: 56, sm: 66, md: 80 },
          borderRadius: "50%",
          background: `${color}22`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: isTablet ? 0 : { xs: 0, md: 2 },
          mr: 0,
          boxShadow: active
            ? `0 0 25px ${color}`
            : `0 0 10px ${color}55`,
          position: "relative",
          flexShrink: 0, // 👈 IMPORTANT
        }}
      >
        {icon}

        {completed && (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              bottom: -4,
              right: -4,
              color: "#00e676",
              fontSize: 22,
              background: "black",
              borderRadius: "50%",
            }}
          />
        )}
      </Box>

      <Stack
        spacing={isTablet ? 0.5 : { xs: 0.5, md: 1.2 }}
        sx={{
          width: "100%",
          minWidth: 0,
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            color: "white",
            fontWeight: 800,
            mb: isTablet ? 0.5 : { xs: 0.4, md: 1.5 },
            letterSpacing: 1.2,
            fontSize: isTablet ? "1.4rem" : { xs: "1.15rem", sm: "1.4rem", md: "1.6rem" },
          }}
        >
          {title}
        </Typography>

        {statusLabel && (
          <Typography
            sx={{
              alignSelf: isTablet ? "flex-start" : { xs: "flex-start", md: "center" },
              color: completed ? "#8ff0b3" : active ? "#fff" : "rgba(255,255,255,0.72)",
              fontWeight: 900,
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              px: 1,
              py: 0.35,
              borderRadius: 1,
              border: "1px solid rgba(255,255,255,0.18)",
              backgroundColor: "rgba(255,255,255,0.07)",
              mb: 1,
            }}
          >
            {statusLabel}
          </Typography>
        )}

        {items.map((item, idx) => (
          <Typography
            key={idx}
            sx={{
              color: "#e8e8e8",
              fontWeight: 500,
              fontSize: isTablet ? "1.05rem" : { xs: "0.92rem", sm: "1.05rem", md: "1.15rem" },
              lineHeight: 1.35,
            }}
          >
            • {item}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};